<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue', 'selectionChange']);

const editor = ref(null);
const isEmpty = ref(true);

// Vérifier l'état du formatage au curseur actuel - définition de fonction hoistée
function checkFormatState() {
  if (!editor.value || !document.activeElement) return;

  // Ne vérifier que si l'éditeur est actif
  if (!editor.value.contains(document.activeElement)) return;

  const states = {
    bold: document.queryCommandState('bold'),
    italic: document.queryCommandState('italic'),
    underline: document.queryCommandState('underline'),
    strikethrough: document.queryCommandState('strikeThrough'),
    alignLeft: document.queryCommandState('justifyLeft'),
    alignCenter: document.queryCommandState('justifyCenter'),
    alignRight: document.queryCommandState('justifyRight'),
  };

  emit('selectionChange', states);
}

// Gérer les changements de contenu
const handleInput = () => {
  if (!editor.value) return;
  const content = editor.value.innerHTML;
  isEmpty.value = editor.value.textContent.trim() === '';
  emit('update:modelValue', content);
  checkFormatState();
};

// Initialiser le contenu
onMounted(() => {
  if (editor.value && props.modelValue) {
    editor.value.innerHTML = props.modelValue;
    isEmpty.value = !props.modelValue;
  }

  // Écouter les changements de sélection
  document.addEventListener('selectionchange', checkFormatState);
});

onBeforeUnmount(() => {
  document.removeEventListener('selectionchange', checkFormatState);
});

// Mettre à jour quand le modelValue change de l'extérieur
watch(
  () => props.modelValue,
  newValue => {
    if (editor.value && editor.value.innerHTML !== newValue) {
      editor.value.innerHTML = newValue || '';
      isEmpty.value = !newValue;
    }
  }
);

// Exposer la méthode pour formater depuis l'extérieur
const format = command => {
  editor.value?.focus();
  document.execCommand(command, false, null);
  handleInput();
};

const formatWithValue = (command, value) => {
  editor.value?.focus();
  document.execCommand(command, false, value);
  handleInput();
};

defineExpose({ format, formatWithValue, editor });
</script>

<template>
  <div class="relative">
    <div
      ref="editor"
      contenteditable="true"
      class="w-full min-h-[120px] px-3 py-2.5 text-sm rounded-lg border-2 border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand transition-colors resize-y overflow-auto"
      :class="{ 'text-n-slate-10': isEmpty }"
      @input="handleInput"
      @focus="
        isEmpty &&
          editor &&
          (editor.textContent === '' ? (editor.innerHTML = '') : null)
      "
      @blur="handleInput"
      @mouseup="checkFormatState"
      @keyup="checkFormatState"
    />
    <div
      v-if="isEmpty && placeholder"
      class="absolute top-2.5 left-3 text-sm text-n-slate-10 pointer-events-none"
    >
      {{ placeholder }}
    </div>
  </div>
</template>
